import { DateTime } from 'luxon';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { PortType, RootState } from 'types';
import { OnChangeType } from 'types/functions';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';
import { SingleDatePicker } from './../../react-dates';
import Icon from './Icon';

interface InlineDateAndTimeSelectProps {
    className?: string;
    name: string;
    id?: string;
    placeholder?: string;
    dateTimeFormat: DateTimeFormat;
    value: DateTimeFormat | null | undefined;
    onChange: OnChangeType;
    today: DateTimeFormat;
    minDate: DateTimeFormat;
    maxDate: DateTimeFormat;
    disabled?: boolean;
    port: PortType;
}

interface InlineDateAndTimeSelectState {
    openSelect: boolean;
    value: DateTimeFormat | null;
    date: DateTimeFormat;
    dateSelect: boolean;
    timeSelect: boolean;
}

class InlineDateAndTimeSelect extends Component<InlineDateAndTimeSelectProps, InlineDateAndTimeSelectState> {
    private dropdownRef = React.createRef<HTMLDivElement>();
    private parentRef = React.createRef<HTMLDivElement>();

    constructor(props: InlineDateAndTimeSelectProps) {
        super(props);
        this.state = {
            value: null,
            date: props.today,
            openSelect: false,
            dateSelect: false,
            timeSelect: false,
        };
    }

    static getDerivedStateFromProps(props: InlineDateAndTimeSelectProps, state: InlineDateAndTimeSelectState) {
        if (props.value !== state.value) {
            return {
                date: props?.value?.clone(),
                value: props.value,
            };
        }

        return null;
    }

    componentDidMount() {
        document.addEventListener('click', this.handleClickOutside);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
    }

    handleClickOutside = (event: MouseEvent) => {
        if (
            this.dropdownRef.current &&
            !this.dropdownRef.current.contains(event.target as Node) &&
            this.parentRef.current &&
            !this.parentRef.current.contains(event.target as Node)
        ) {
            this.setState({ openSelect: false }, () => {
                this.changeValue(false);
            });
        }
    };

    openSelect = () => {
        if (this.props.disabled) {
            return;
        }
        this.setState({
            dateSelect: this.state.openSelect,
            timeSelect: this.state.openSelect,
            openSelect: !this.state.openSelect,
        });
    };

    generateTimeSlots = () => {
        const times: string[] = [];

        for (let hours = 0; hours < 24; hours++) {
            for (let minutes = 0; minutes < 60; minutes += 30) {
                const formattedTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
                times.push(formattedTime);
            }
        }

        return times;
    };

    setTime = (value) => {
        const [hours, minutes] = value.split(':').map(Number);
        //@ts-ignore
        const valueWithoutOffset = this.state?.date.setHoursAndMinutes(hours, minutes);
        //@ts-ignore

        this.setState(
            {
                //@ts-ignore
                date: valueWithoutOffset,
                timeSelect: true,
            },
            () => {
                this.changeValue(false);
            }
        );
    };

    setDate = (date) => {
        this.setState(
            {
                date: this.props.dateTimeFormat
                    .fromLuxon(this.applyLocalOffset(date, 'minus'))
                    .setHoursAndMinutes(
                        this.applyLocalOffset(this.state.date.date).hour,
                        this.applyLocalOffset(this.state.date.date).minute
                    )
                    .roundToNearest30Minutes(),
                dateSelect: true,
            },
            () => {
                this.changeValue(true);
            }
        );
    };

    changeValue = (close: boolean) => {
        this.setState({
            openSelect: close,
        });
        this.props.onChange(this.props.name, this.state.date?.clone());
    };

    renderMonthElement = ({ month, onMonthSelect, onYearSelect }) => {
        const currentMonth = month.month; // Luxon uses `.month` (1–12)
        const currentYear = month.year;

        const startYear = this.props.minDate?.date.year;
        const endYear = this.props.maxDate?.date.year;

        const years: number[] = [];
        for (let y = endYear; y >= startYear; y--) {
            years.push(y);
        }

        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div>
                    <select
                        value={currentMonth - 1} // Convert to 0-indexed for select
                        onChange={
                            (e) => onMonthSelect(month, parseInt(e.target.value, 10) + 1) // Add 1 back for Luxon
                        }
                    >
                        {Array.from({ length: 12 }, (_, i) => (
                            <option key={i} value={i}>
                                {DateTime.local()
                                    .set({ month: i + 1 })
                                    .toFormat('LLLL')}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <select value={currentYear} onChange={(e) => onYearSelect(month, parseInt(e.target.value, 10))}>
                        {years.map((x, i) => {
                            const year = DateTime.now().year - i;
                            return (
                                <option key={year} value={year}>
                                    {x}
                                </option>
                            );
                        })}
                    </select>
                </div>
            </div>
        );
    };

    applyLocalOffset = (utcDate: DateTime, op: 'plus' | 'minus' = 'plus'): DateTime => {
        const originZone = this.props.port.time;

        // Get the offset of the origin zone at the exact utcDate
        const offset = utcDate.setZone(originZone).offset;

        // Apply the offset based on the operation
        return op === 'plus' ? utcDate.plus({ minutes: offset }) : utcDate.minus({ minutes: offset });
    };

    render() {
        const { className = 'w-full', placeholder } = this.props;
        const { openSelect, value } = this.state;
        return (
            <div className="w-full relative" ref={this.parentRef}>
                <div
                    ref={this.dropdownRef}
                    className={twclsx(
                        'relative',
                        'py-1',
                        'px-2',
                        'focus:border-focused',
                        'focus:shadow-input',
                        'focus:outline-none',
                        className
                    )}
                >
                    <div onClick={this.openSelect} className="flex justify-between align-middle cursor-pointer ">
                        <div>
                            {value
                                ? this.state.date?.clone().formatDateTimeToString('yyyy-MM-dd HH:mm:ss Z')
                                : translate(placeholder || translate('Select Date and time'))}{' '}
                        </div>

                        <div>
                            <Icon className="w-4 h-4">
                                <path d="M4 7a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12z"></path>
                                <path d="M16 3v4"></path>
                                <path d="M8 3v4"></path>
                                <path d="M4 11h16"></path>
                                <path d="M11 15h1"></path>
                                <path d="M12 15v3"></path>
                            </Icon>
                        </div>
                    </div>
                </div>
                <div className="w-full bg-white ">
                    <div className="py-3 flex gap-1 rounded-md">
                        <div className="w-singleCalander ">
                            <SingleDatePicker
                                numberOfMonths={1}
                                id="dateInput"
                                renderMonthElement={(data) => {
                                    return this.renderMonthElement(data);
                                }}
                                hideKeyboardShortcutsPanel
                                displayFormat={() => 'dd/MM/yyyy'}
                                // eslint-disable-next-line @typescript-eslint/no-empty-function
                                onFocusChange={() => {}}
                                isOutsideRange={(date) => {
                                    return !this.props.dateTimeFormat
                                        .fromLuxon(this.applyLocalOffset(date, 'minus'))
                                        //@ts-ignore
                                        .isBetween(
                                            this.applyLocalOffset(this.props.minDate.date, 'plus'),
                                            this.applyLocalOffset(this.props.maxDate.date, 'plus')
                                        );
                                }}
                                verticalSpacing={0}
                                showClearDate={() => false}
                                minimumNights={0}
                                keepOpenOnDateSelect={() => true}
                                noBorder={true}
                                date={this.applyLocalOffset(this.state.date?.date)}
                                focused={true}
                                onDateChange={this.setDate}
                                autoFocus
                            />
                        </div>
                        <div className="w-96">
                            <div
                                className="w-full flex flex-col flex-wrap px-1 my-1 gap-1"
                                style={{ marginTop: 64, height: 204 }}
                            >
                                {(() => {
                                    const { today, dateTimeFormat } = this.props;
                                    const { date } = this.state;
                                    if (!today || !date) return null;

                                    const roundedToday = dateTimeFormat.now().roundToNearest5Minutes();
                                    const todayTime = roundedToday.formatDateTimeToString('HH:mm');
                                    const todayDate = roundedToday.formatDateTimeToString('dd MMM yyyy');

                                    const selectedTime = date.formatDateTimeToString('HH:mm');
                                    const selectedDate = date.formatDateTimeToString('dd MMM yyyy');
                                    const isSameDay = selectedDate === todayDate;

                                    return this.generateTimeSlots().map((time) => {
                                        const [HH, MM] = time.split(':').map(Number);
                                        const [todayHH, todayMM] = todayTime.split(':').map(Number);
                                        // convert both to total minutes
                                        const totalMinutes = HH * 60 + MM;
                                        const todayTotalMinutes = todayHH * 60 + todayMM;

                                        const isFuture = totalMinutes > todayTotalMinutes && isSameDay;
                                        const isPast = totalMinutes < todayTotalMinutes && isSameDay;
                                        const isCurrentTime = totalMinutes === todayTotalMinutes && isSameDay;

                                        const isSelectedTime = selectedTime === time;

                                        const currentHour = Number(date.clone().formatDateTimeToString('HH'));
                                        const currentMinute = Number(date.clone().formatDateTimeToString('mm'));

                                        const isFirstHalf = currentMinute > 0 && currentMinute < 30;
                                        const isSecondHalf = currentMinute > 30 && currentMinute < 60;

                                        const shouldShowLine =
                                            isSameDay &&
                                            HH === currentHour &&
                                            ((isFirstHalf && MM === 0) || (isSecondHalf && MM === 30));

                                        const baseClasses = [
                                            'text-center',
                                            'flex',
                                            'relative',
                                            'cursor-pointer',
                                            'rounded-md',
                                            'px-1',
                                            'border',
                                            'border-white',
                                            'text-black',
                                        ];

                                        if (isSameDay && isPast) baseClasses.push('hover:border-blue-500');
                                        if (isCurrentTime) baseClasses.push('bg-green-500 text-white');
                                        if (isSelectedTime) baseClasses.push('bg-blue-500 text-white');

                                        return (
                                            <div key={time} className={twclsx(baseClasses)}>
                                                <div
                                                    onClick={() => {
                                                        if (isFuture) return;
                                                        this.setTime(time);
                                                    }}
                                                    className={twclsx(isFuture ? 'text-gray-400' : 'text-black')}
                                                >
                                                    {time}
                                                </div>

                                                {isCurrentTime && (
                                                    <div className="ml-1 text-xs text-black">{translate('now')}</div>
                                                )}

                                                {shouldShowLine && (
                                                    <div className="w-full h-1 border-b top-5 -left-1 border-black absolute"></div>
                                                )}
                                            </div>
                                        );
                                    });
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const inlineDateAndTimeSelectStateToProps = (state: RootState) => ({
    dateTimeFormat: state.port.dateTimeFormat,
    port: state.port.port,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const inlineDateAndTimeSelectDispatchToProps = () => ({});

export default connect(
    inlineDateAndTimeSelectStateToProps,
    inlineDateAndTimeSelectDispatchToProps
)(InlineDateAndTimeSelect);
